// Quick guide — Stress & written accents (A1). The default stress rules, the
// three accent marks (´ ^ ~), nasal vowels, the grave/crase à, and diacritics.
// Hand-drawn paper style with audio on examples.
import { speakButtonHTML } from "../lib/audio.js";

function ex(words) {
  return `<div class="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">${words
    .map((w) => `<span class="text-sage-800">${w}${speakButtonHTML(w.replace(/\s*\(.*/, ""))}</span>`)
    .join("")}</div>`;
}

export function stressAccentsView(app) {
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <h2 class="text-4xl font-script text-sage-800 mb-1">Stress & Accents ´</h2>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">Portuguese stress is largely rule-based. Learn the defaults, and the accents (´ ^ ~ \`) mark the exceptions and the nasal sounds.</p>
      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg space-y-5">

        <div>
          <h3 class="text-2xl text-emerald-700 sketch-underline inline-block">1 · The two default rules</h3>
          <p class="text-sage-700 mt-1">No accent needed when the stress follows these:</p>
          <p class="text-sage-700 mt-2">Ends in <b>a, e, o</b> (also -am, -em, -s) → stress the <b>second-to-last</b> syllable.</p>
          ${ex(["casa", "falam", "livros", "jovem"])}
          <p class="text-sage-700 mt-2">Ends in <b>i, u</b> or a consonant like <b>-l, -r, -z</b> → stress the <b>last</b> syllable.</p>
          ${ex(["falar", "feliz", "comum", "capaz"])}
        </div>

        <div>
          <h3 class="text-2xl text-amber-700 sketch-underline inline-block">2 · The accent marks the exception</h3>
          <p class="text-sage-700 mt-1">When the stress lands where the rules <i>don't</i> predict, you write an accent (´ acute on open vowels, ^ circumflex on closed ones).</p>
          ${ex(["sofá", "café", "avô", "fácil", "lápis", "açúcar"])}
        </div>

        <div>
          <h3 class="text-2xl text-sky-700 sketch-underline inline-block">3 · Proparoxytones — always an accent</h3>
          <p class="text-sage-700 mt-1">Stress on the <b>third-from-last</b> syllable <b>always</b> takes an accent.</p>
          ${ex(["música", "médico", "rápido", "público", "câmara"])}
        </div>

        <div>
          <h3 class="text-2xl text-rose-600 sketch-underline inline-block">4 · Accents that change meaning</h3>
          <p class="text-sage-700 mt-1">Same spelling, accent flips the meaning (diacritics):</p>
          <div class="flex flex-wrap gap-x-6 gap-y-1 mt-1 text-base">
            <span><b>pôr</b> to put · <b>por</b> by / for</span>
            <span><b>é</b> is · <b>e</b> and</span>
            <span><b>pôde</b> could · <b>pode</b> can</span>
            <span><b>avô</b> grandfather · <b>avó</b> grandmother</span>
            <span><b>está</b> is · <b>esta</b> this</span>
            <span><b>têm</b> they have · <b>tem</b> he has</span>
            <span><b>sé</b> cathedral · <b>se</b> if / oneself</span>
          </div>
        </div>

        <div>
          <h3 class="text-2xl text-violet-700 sketch-underline inline-block">5 · Nasal vowels — the til (~)</h3>
          <p class="text-sage-700 mt-1">The til (~) marks a nasal vowel, sounded through the nose. It rides on <b>ã</b> and <b>õ</b>, often in plurals and verb endings.</p>
          ${ex(["mãe", "pão", "irmã", "lições", "põe", "mãos"])}
        </div>

        <div>
          <h3 class="text-2xl text-teal-700 sketch-underline inline-block">6 · The grave accent — à (crase)</h3>
          <p class="text-sage-700 mt-1">The grave accent appears when the preposition <b>a</b> merges with the article <b>a</b>: a + a = <b>à</b>. It marks the contraction, not stress.</p>
          ${ex(["à", "às", "àquele", "àquela", "àquilo"])}
        </div>

      </div>
      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">Rule of thumb: if the stress falls where the default rules don't predict — or a vowel is nasal — you'll see an accent.</p>
    </section>`;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
